import React from 'react';
import PropTypes from 'prop-types';

import './style.less';

// Hardcoded things for now
const SIDE_MARGINS = 300;
const WIDTH_PER_CATEGORY = 108;

function normalizeToClassName(name) {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '-');
}

function splitList(list, count) {
  if (!list || list.length === 0) {
    return {visible: null, hidden: null};
  }

  if (list.length > count) {
    count -= 1;
  }

  let visible = list.slice(0, count);
  let hidden = list.slice(count);
  return {visible, hidden};
}

class CategoryBarDropdown extends React.Component {
  static propTypes = {
    options: PropTypes.arrayOf(PropTypes.string),
    value: PropTypes.string,
    expand: PropTypes.bool.isRequired,
    activeClass: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    onPlaceholderMouseOver: PropTypes.func,
    onExpand: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.onPlaceholderMouseOver = this.onPlaceholderMouseOver.bind(this);
  }

  onPlaceholderMouseOver() {
    if (this.props.onPlaceholderMouseOver) {
      this.props.onPlaceholderMouseOver();
    }
    this.props.onExpand(true);
  }

  render() {
    let hasActive = false;
    let optionItems = this.props.options.map((option) => {
      let active = option === this.props.value;
      hasActive = hasActive || active;
      let className = `option ${active ? this.props.activeClass : ''}`;
      return <div key={option}
                  className={className}
                  onClick={this.props.onClick.bind(this, option, false)}>{option}</div>
    });

    return (
      <div
        className={`category-bar-dropdown-component ${this.props.className} ${this.props.expand ? 'expand' : ''} ${hasActive ? this.props.activeClass : ''}`}
        onMouseLeave={this.props.onExpand.bind(this, false)}>
        <div className="placeholder"
             onMouseOver={this.onPlaceholderMouseOver}>
          {this.props.placeholder + (this.props.value ? ' / ' + this.props.value : '')}
        </div>
        <div className={"dropdown"}>
          {optionItems}
        </div>
      </div>);
  }
}


export default class CategoryBar extends React.Component {
  static propTypes = {
    onCategory: PropTypes.func,
    categories: PropTypes.object.isRequired,
    filters: PropTypes.arrayOf(PropTypes.object),
    activeCategory: PropTypes.string,
    activeSubcategory: PropTypes.string,
    activeFilter: PropTypes.object
  };

  static contextTypes = {
    router: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.onScroll = this.onScroll.bind(this);
    this.onCategory = this.onCategory.bind(this);
    this.onSubcategory = this.onSubcategory.bind(this);
    this.onFilter = this.onFilter.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSubcategoryDropdown = this.onSubcategoryDropdown.bind(this);
    this.onCategoryDropdown = this.onCategoryDropdown.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.width = window.innerWidth;
    this.categories = {};
    this.collapsed = false;
    this.$categoryBar = null;

    this.state = this.createState(props);
  }

  createState(props) {
    this.categories = props.categories;
    let categoryNames = Object.keys(this.categories);
    let activeCategory = null;
    let activeSubcategories = null;
    let activeSubcategory = null;

    // Only set active category/subcategory if defined in `this.categories`
    if (props.activeCategory) {
      let subcategories = this.categories[props.activeCategory];
      if (subcategories) {
        activeCategory = props.activeCategory;
        activeSubcategories = subcategories;
      }

      if (props.activeSubcategory) {
        if (subcategories) {
          let isValidSubcategory = subcategories.findIndex(subcategory => subcategory === props.activeSubcategory) >= 0;
          if (isValidSubcategory) {
            activeSubcategory = props.activeSubcategory;
          }
        }
      }
    }

    let threshold = this.width - SIDE_MARGINS;
    let count = this.updateVisibleCount(threshold, categoryNames);

    let {
      visible: visibleCategories,
      hidden: hiddenCategories
    } = splitList(categoryNames, count);
    let {
      visible: visibleSubcategories,
      hidden: hiddenSubcategories
    } = splitList(activeSubcategories, count);

    let activeFilter = props.activeFilter || null;

    return {
      activeCategory,
      activeSubcategory,
      activeFilter,
      visibleCategories,
      hiddenCategories,
      visibleSubcategories,
      hiddenSubcategories,
      categoryDropdownExpand: false,
      subcategoryDropdownExpand: false,
      tmpCategory: null,
      tmpVisibleSubcategories: null,
      tmpHiddenSubcategories: null
    };
  }

  getWidth(item) {
    return this.clientWidths[item];
  }

  updateVisibleCount(threshold, items) {
    this.visibleCount = Math.min(Math.floor(threshold / WIDTH_PER_CATEGORY, items.length));
    return this.visibleCount;
  }

  componentWillReceiveProps(newProps) {
    if (newProps.categories !== this.props.categories) {
      this.setState(this.createState(newProps));
    }

    if (newProps.activeCategory !== this.props.activeCategory
      || newProps.activeFilter !== this.props.activeFilter
      || newProps.activeSubcategory !== this.props.activeSubcategory) {
      // naive approach, assume category is valid
      this.setState({
        activeCategory: newProps.activeCategory,
        activeSubcategory: newProps.activeSubcategory,
        activeFilter: newProps.activeFilter
      })
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize); // re-render visible
    window.addEventListener('scroll', this.onScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  onCategoryDropdown(visible) {
    let subcategoryDropdownExpand = this.state.subcategoryDropdownExpand;
    if (visible && subcategoryDropdownExpand) {
      subcategoryDropdownExpand = false;
    }
    this.setState({
      categoryDropdownExpand: visible,
      subcategoryDropdownExpand
    });
  }

  onSubcategoryDropdown(visible) {
    let categoryDropdownExpand = this.state.categoryDropdownExpand;
    if (visible && categoryDropdownExpand) {
      categoryDropdownExpand = false;
    }
    this.setState({
      subcategoryDropdownExpand: visible,
      categoryDropdownExpand
    });
  }

  onCategory(category, clear) {
    let activeCategory = category;
    let activeSubcategory = null;
    let activeFilter = null;
    let categoryDropdownExpand = false;
    let subcategoryDropdownExpand = false;
    let visibleSubcategories = null;
    let hiddenSubcategories = null;

    if (clear || category !== this.state.activeCategory) {
      let subcategories = this.categories[activeCategory];

      let {
        visible,
        hidden
      } = splitList(subcategories, this.visibleCount);
      visibleSubcategories = visible;
      hiddenSubcategories = hidden;
    }

    this.setState({
      activeCategory,
      activeSubcategory,
      activeFilter,
      visibleSubcategories,
      hiddenSubcategories,
      categoryDropdownExpand,
      subcategoryDropdownExpand
    });

    this.props.onSelect(activeCategory, activeSubcategory, activeFilter);
  }

  onSubcategory(subcategory, clear) {
    let activeSubcategory = subcategory;
    if (!clear && subcategory === this.state.activeSubcategory && this.state.tmpCategory === this.state.activeCategory) {
      // no-op
      return;
    }

    let activeCategory = this.state.activeCategory;
    let visibleSubcategories = this.state.visibleSubcategories;
    let hiddenSubcategories = this.state.hiddenSubcategories;

    if (this.state.tmpCategory) {
      activeCategory = this.state.tmpCategory;
      visibleSubcategories = this.state.tmpVisibleSubcategories;
      hiddenSubcategories = this.state.tmpHiddenSubcategories;
    }

    this.setState({
      activeCategory,
      activeSubcategory,
      visibleSubcategories,
      hiddenSubcategories,
      categoryDropdownExpand: false,
      subcategoryDropdownExpand: false,
      tmpCategory: null,
      tmpVisibleSubcategories: null,
      tmpHiddenSubcategories: null
    });

    this.props.onSelect(activeCategory, activeSubcategory, this.state.activeFilter);
  }

  onFilter(filter) {
    if (this.state.activeFilter && this.state.activeFilter.value === filter.value) {
      filter = null;
    }

    this.setState({
      activeFilter: filter,
      categoryDropdownExpand: false,
      subcategoryDropdownExpand: false,
    });

    this.props.onSelect(this.state.activeCategory, this.state.activeSubcategory, filter);
  }

  onResize() {
    this.width = window.innerWidth;
    let threshold = this.width - SIDE_MARGINS;
    let categoryNames = Object.keys(this.categories);
    let subcategories = this.state.activeCategory ? this.categories[this.state.activeCategory] : null;

    let count = this.updateVisibleCount(threshold, categoryNames);
    let {
      visible: visibleCategories,
      hidden: hiddenCategories
    } = splitList(categoryNames, count);
    let {
      visible: visibleSubcategories,
      hidden: hiddenSubcategories
    } = splitList(subcategories, count);

    this.setState({
      visibleCategories,
      hiddenCategories,
      visibleSubcategories,
      hiddenSubcategories
    });
  }

  onScroll() {
    const distance = window.pageYOffset || window.document.documentElement.scrollTop;
    const minimizeThreshold = this.$categoryBar.clientHeight;

    if (distance < minimizeThreshold) {
      this.$categoryBar.classList.remove('collapse');
      this.collapsed = false;
    } else {
      if (!this.$categoryBar.classList.contains('collapse')) {
        this.$categoryBar.classList.add('collapse');
        this.collapsed = true;

        // only hide dropdowns if scolling causes collapse
        if (this.state.categoryDropdownExpand || this.state.subcategoryDropdownExpand) {
          this.setState({
            categoryDropdownExpand: false,
            subcategoryDropdownExpand: false,
          });
        }
      }
    }
  }

  onMouseOver(tmpCategory) {
    if (this.collapsed) {
      return;
    }

    if (tmpCategory === this.state.activeCategory) {
      this.setState({
        tmpCategory: null,
        tmpVisibleSubcategories: null,
        tmpHiddenSubcategories: null
      });
      return;
    }

    let subcategories = this.categories[tmpCategory];
    let {
      visible: tmpVisibleSubcategories,
      hidden: tmpHiddenSubcategories
    } = splitList(subcategories, this.visibleCount);
    this.setState({
      tmpCategory,
      tmpVisibleSubcategories,
      tmpHiddenSubcategories
    });
  }

  onMouseLeave() {
    if (this.state.tmpCategory) {
      this.setState({
        tmpCategory: null,
        tmpVisibleSubcategories: null,
        tmpHiddenSubcategories: null
      })
    }
  }

  render() {
    let visibleCategoryItems = this.state.visibleCategories
      .map((c, index) => {
        let normalized = normalizeToClassName(c);
        let className = `category ${(c === this.state.activeCategory) ? 'active' : ''}`;
        return <div key={`${normalized}-${index}`}
                    className={className}
                    onMouseOver={this.onMouseOver.bind(this, c)}
                    onClick={this.onCategory.bind(this, c, false)}>
          <div>{c}</div>
        </div>;
      });

    let hiddenCategoryView = null;
    if (this.state.hiddenCategories.length > 0) {
      let activeCategoryIsHidden = (
      this.state.activeCategory &&
      this.state.hiddenCategories.findIndex(h => h === this.state.activeCategory) >= 0);

      let selectedValue = activeCategoryIsHidden ? this.state.activeCategory : null;
      hiddenCategoryView = (
        <CategoryBarDropdown value={selectedValue}
                             placeholder="...MORE"
                             className="category"
                             activeClass="active"
                             expand={this.state.categoryDropdownExpand}
                             onPlaceholderMouseOver={this.onMouseLeave}
                             onExpand={this.onCategoryDropdown}
                             onClick={this.onCategory}
                             options={this.state.hiddenCategories}/>
      );
    }

    let showSubcategoryBar = (this.state.activeCategory !== null
    && this.state.visibleSubcategories !== null
    && this.state.hiddenSubcategories !== null);

    if (this.state.tmpCategory) {
      showSubcategoryBar = this.state.tmpVisibleSubcategories !== null;
    }

    let hideSubcategoryClassName = showSubcategoryBar ? '' : 'no-subcategories';

    let visibleSubcategories = this.state.tmpVisibleSubcategories ?
      this.state.tmpVisibleSubcategories : this.state.visibleSubcategories;
    let hiddenSubcategories = this.state.tmpHiddenSubcategories ?
      this.state.tmpHiddenSubcategories : this.state.hiddenSubcategories;

    let visibleSubcategoryItems = visibleSubcategories ?
      visibleSubcategories
        .map((sub) => {
          let className = `subcategory ${(sub === this.state.activeSubcategory && !this.state.tmpCategory) ? 'active' : ''}`;
          return <div className={className} key={sub}
                      onClick={this.onSubcategory.bind(this, sub, false, !!this.state.tmpCategory)}>
            <div>{sub}</div>
          </div>;
        })
      : null;

    let hiddenSubcategoryView = null;
    if (hiddenSubcategories && hiddenSubcategories.length > 0) {
      let activeSubcategoryIsHidden = (
      !this.state.tmpCategory &&
      this.state.activeSubcategory &&
      this.state.hiddenSubcategories
        .findIndex(h => h === this.state.activeSubcategory) >= 0);

      let selectedValue = activeSubcategoryIsHidden ? this.state.activeSubcategory : null;
      hiddenSubcategoryView = (
        <CategoryBarDropdown value={selectedValue}
                             placeholder="...More"
                             activeClass="active"
                             className="subcategory"
                             expand={this.state.subcategoryDropdownExpand}
                             onExpand={this.onSubcategoryDropdown}
                             onClick={this.onSubcategory}
                             options={hiddenSubcategories}/>
      );
    }

    let path = [{name: 'Home', onClick: this.onCategory.bind(this, this.state.activeCategory, false)}];
    if (this.state.activeCategory) {
      path.push({
        name: this.state.activeCategory,
        onClick: this.onCategory.bind(this, this.state.activeCategory, true)
      });
    }
    if (this.state.activeSubcategory) {
      path.push({
        name: this.state.activeSubcategory,
        onClick: this.onSubcategory.bind(this, this.state.activeSubcategory, true)
      });
    }

    let filterItems = this.props.filters ?
      this.props.filters.map((filter) => {
        let active = (this.state.activeFilter && this.state.activeFilter.value === filter.value);
        let className = `filter ${active ? 'active' : ''}`;
        return <div className={className} key={filter.name}
                    onClick={this.onFilter.bind(this, filter)}>
          <img role="presentation" className="icon" style={{height: filter.height + 'px'}} src={filter.icon}/>
          <span>{filter.name}</span>
        </div>;
      }) : null;

    return (<nav role="navigation"
                 className={`category-bar-component ${hideSubcategoryClassName}`}
                 ref={(div) => {
                   this.$categoryBar = div;
                 }}>
      <div className="category-bar"
           onScroll={this.onScroll}>
        <div onMouseLeave={this.onMouseLeave}>
          <div className="categories">
            {visibleCategoryItems}
            {hiddenCategoryView}
          </div>
          <div className="subcategories">
            {visibleSubcategoryItems}
            {hiddenSubcategoryView }
          </div>
        </div>
        <div className="filters">
          {filterItems}
        </div>
      </div>
    </nav>);
  }
}


/** WEBPACK FOOTER **
 ** ./src/components/CategoryBar/index.jsx
 **/